export const en = {
  languageSwitcher: {
    ariaLabel: 'Choose interface language',
    enShort: 'EN',
    enTitle: 'English',
    frShort: 'FR',
    frTitle: 'French',
  },
  publicNav: {
    signIn: 'Sign in',
    back: 'Back',
  },
  auth: {
    brandTagline: 'CV ranking',
    common: {
      email: 'Email address',
      emailPlaceholder: 'you@company.com',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      sending: 'Sending...',
      signIn: 'Sign in',
    },
    errors: {
      requiredEmail: 'Please enter your email address.',
      requiredEmailPassword: 'Please enter your email and password.',
      requiredFields: 'Please fill in all fields.',
      requiredName: 'Please enter your name.',
      passwordMinimum: 'Password must be at least 8 characters.',
      passwordMismatch: 'Passwords do not match.',
      accountLookupSafe: 'If an account exists for this email address, a reset code will be sent.',
      incorrectCredentials: 'Incorrect email or password.',
      unconfirmed: 'Your account is not verified yet. Enter the code from your email to continue.',
      usernameExists: 'An account already exists with this email address.',
      invalidPassword: 'Password must be at least 8 characters with uppercase, lowercase and numbers.',
      invalidCode: 'The verification code is incorrect. Please try again.',
      expiredCode: 'The verification code has expired. Please request a new one.',
      tooManyAttempts: 'Too many attempts. Please wait a moment and try again.',
      generic: 'Something went wrong. Please try again.',
    },
    signIn: {
      verifiedNotice: 'Your email is verified. Sign in to continue.',
      welcome: 'Welcome back',
      subtitle: 'Sign in to your WorkflowPro account',
      verifyAccount: 'Verify account',
      resendCode: 'Resend code',
      passwordPlaceholder: 'Enter your password',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      createFree: 'Create one free',
      legalPrefix: 'By signing in you agree to our',
      terms: 'Terms of Service',
      and: 'and',
      privacy: 'Privacy Policy',
    },
    signUp: {
      title: 'Create your account',
      subtitle: 'Start ranking CVs with WorkflowPro for free',
      name: 'Your name',
      namePlaceholder: 'Jane Smith',
      company: 'Company name',
      companyPlaceholder: 'Acme Corp',
      workEmail: 'Work email',
      createPassword: 'Create a strong password',
      confirmPassword: 'Confirm password',
      repeatPassword: 'Repeat your password',
      createAccount: 'Create free account',
      legalPrefix: 'By creating an account you agree to our',
      alreadyAccount: 'Already have an account?',
      verifyAndStart: 'Verify and get started',
      strength: {
        minimum: 'At least 8 characters',
        uppercase: 'Uppercase letter',
        lowercase: 'Lowercase letter',
        number: 'Number',
        weak: 'Weak',
        fair: 'Fair',
        good: 'Good',
        strong: 'Strong',
      },
    },
    verification: {
      title: 'Check your email',
      instructions: 'Enter the 6-digit verification code we sent to',
      emailFallback: 'your email address',
      code: 'Verification code',
      verifyEmail: 'Verify email',
      newCodeSent: 'A new verification code has been sent. Please check your email.',
      sendNewCode: 'Send a new code',
      noCode: "Didn't receive the code?",
      resendCode: 'Resend code',
      successTitle: 'Email verified',
      successBody: 'Your email address is verified. Sign in to continue to WorkflowPro.',
    },
    forgotPassword: {
      back: 'Back to sign in',
      title: 'Reset your password',
      requestInstructions: 'Enter your email address. If an account exists, a reset code will be sent.',
      sendCode: 'Send reset code',
      genericNotice: 'If an account exists for this email address, a reset code will be sent.',
      enterCode: 'Enter reset code',
      codeInstructions: 'Use the code received by email if an account matches this address.',
      resetCode: 'Reset code',
      newPassword: 'New password',
      newPasswordPlaceholder: 'New password',
      resetAction: 'Reset password',
      doneTitle: 'Password reset',
      doneBody: 'Your password has been reset successfully.',
      signInNewPassword: 'Sign in with new password',
    },
  },
  landing: {
    preview: {
      topCandidates: 'Top 2 candidates to review',
      jobTitle: 'Senior Software Engineer',
      rankedCount: '4 candidates ranked',
      topMatch: 'Top match: 94%',
      years: '{count} years',
      whyExample: 'Matches 8/10 required skills - Strong AWS experience',
      scoreBands: {
        excellent: 'Excellent',
        good: 'Good',
        partial: 'Partial',
        poor: 'Poor',
      },
      reviewBadge: 'Review first',
      whyPrefix: 'Match',
      bottomNote: 'Candidate ranking preview - Recruiter decision support',
      export: 'Export shortlist',
    },
    faq: [
      {
        q: 'Do I need to create an account?',
        a: "No. You can try WorkflowPro for free without creating an account. Create an account only when you're ready to save and manage your hiring projects.",
      },
      {
        q: 'How does WorkflowPro rank candidates?',
        a: 'WorkflowPro compares each CV against your job requirements and provides an AI-powered ranking with a detailed score breakdown and supporting evidence.',
      },
      {
        q: 'What file formats are supported?',
        a: 'You can upload PDF and DOCX CVs.',
      },
      {
        q: 'Is my data secure?',
        a: 'Your uploaded documents are handled in accordance with our Privacy Policy.',
      },
      {
        q: 'Can I compare candidates?',
        a: 'Yes. Candidate comparison is available on Pro and Business plans.',
      },
      {
        q: 'Can I start for free?',
        a: 'Yes. The Free plan lets you try WorkflowPro on a real hiring need before upgrading.',
      },
    ],
    plans: {
      free: {
        name: 'Free',
        price: '\u20ac0',
        audience: 'For trying WorkflowPro on a real hiring need.',
        cta: 'Start Free',
        features: [
          '1 active job',
          'Up to 5 CVs',
          'AI-powered candidate ranking',
          'Basic score breakdown',
        ],
      },
      starter: {
        name: 'Starter',
        price: '\u20ac39/month',
        audience: 'For independent recruiters and small recruitment agencies.',
        cta: 'Choose Starter',
        features: [
          'Up to 5 active jobs',
          'Up to 400 CVs per month',
          'Includes all Free features',
          'Skills & evidence extraction',
          'Export results',
        ],
      },
      pro: {
        name: 'Pro',
        price: '\u20ac69/month',
        audience: 'For recruiters managing more hiring volume.',
        cta: 'Choose Pro',
        features: [
          'Up to 20 active jobs',
          'Up to 2,000 CVs per month',
          'Includes all Starter features',
          'Candidate comparison',
        ],
      },
      business: {
        name: 'Business',
        price: '\u20ac129/month',
        audience: 'For small recruitment agencies with higher CV volumes.',
        cta: 'Choose Business',
        features: [
          'Up to 100 active jobs',
          'Up to 10,000 CVs per month',
          'Includes all Pro features',
        ],
      },
    },
    navCta: 'Get your shortlist now',
    eyebrow: 'AI-ranked candidate shortlists',
    headline1: 'Identify which candidates',
    headline2: 'are worth reviewing',
    headline3: 'first.',
    subheading: 'Give WorkflowPro a job description and your candidate CVs. It helps you identify which candidates are worth reviewing first. No signup required for your first shortlist.',
    primaryCta: 'Upload CVs & Get Shortlist',
    pricingCta: 'View Pricing',
    trustPrivacy: 'Privacy details available',
    trustRanking: 'Ranking support for screening',
    trustFree: 'First shortlist free',
    trustSetup: 'No setup required',
    proofReview: 'Review shortlist',
    proofReviewLabel: 'with AI-assisted ranking',
    proofReduce: 'Reduce manual review',
    proofReduceLabel: 'with structured candidate comparison',
    proofFree: 'Try free',
    proofFreeLabel: '5 CVs, no card needed',
    proofEu: 'EU-hosted',
    proofEuLabel: 'see Privacy Policy for details',
    howTitle: 'From CVs to shortlist in three simple steps',
    howIntro: "Describe the role you're hiring for, upload your candidate CVs, and let WorkflowPro rank them by fit so you can focus your review on the candidates whose CVs best match the job requirements.",
    step1Title: 'Describe the role and upload your CVs',
    step1Description: 'Enter your job title and job description, then upload your candidate CVs.',
    step2Title: 'WorkflowPro ranks every candidate',
    step2Description: 'Each CV is compared against your job requirements and ranked by overall fit.',
    step3Title: 'Review your shortlist',
    step3Description: 'Review your ranked candidates, compare their strengths, and decide which candidates to review first.',
    benefitsTitle: 'Review candidates with clearer context.',
    benefitsIntro: 'WorkflowPro helps independent recruiters and small recruitment agencies identify candidates worth reviewing first without changing the way they hire.',
    benefit1Title: 'Prioritize your CV review',
    benefit1Description: 'Review ranked candidates instead of reading every CV from top to bottom first.',
    benefit2Title: 'Focus on higher-ranked candidates',
    benefit2Description: 'Surface candidates with higher match scores first so your review starts with the CVs most aligned to the role.',
    benefit3Title: 'Apply a consistent review process',
    benefit3Description: 'Evaluate every candidate against the same job requirements using a consistent process.',
    benefit4Title: 'Start with a job description',
    benefit4Description: 'Enter the role context, upload CVs, and review ranked results in one workflow.',
    pricingTitle: 'Simple, transparent pricing',
    pricingIntro: 'Start free, upgrade when your hiring needs grow. No hidden fees, no long-term commitment.',
    popular: 'Most Popular',
    faqTitle: 'Frequently asked questions',
    finalTitle: 'Ready to prioritize your candidate review?',
    finalDescription: 'Use WorkflowPro to compare candidate CVs against the job description and focus your review on the candidates worth reviewing first. Your first shortlist is free.',
    finalStart: 'Start Free',
    noCard: 'No credit card required.',
    contact: 'Contact',
    pricing: 'Pricing',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    cookies: 'Cookie Policy',
    copyright: '© 2026 WorkflowPro. All rights reserved.',
  },
  anonUpload: {
    toasts: {
      invalidFiles: 'Only PDF and DOCX files are accepted',
      maxReady: '{count} CVs ready to upload',
      addCv: 'Please add at least one CV',
      genericError: 'Something went wrong',
    },
    heading: 'Upload CVs for AI-assisted ranking',
    subheading: 'No account needed. Upload up to 5 CVs and see ranked results as soon as analysis finishes.',
    existingTitle: 'Continue your current shortlist',
    existingDescription: 'Your anonymous results are still saved in this browser.',
    startOver: 'Start over',
    continue: 'Continue',
    jobDetails: 'Job details',
    optionalContext: '(optional context for ranking)',
    jobTitle: 'Job Title',
    jobTitlePlaceholder: 'e.g. Senior Software Engineer',
    jobDescription: 'Job Description',
    jobDescriptionPlaceholder: 'Paste your job description here to give the ranking role context...',
    uploadCvs: 'Upload CVs',
    added: '({count}/{max} added)',
    maxReached: 'Maximum {count} CVs reached',
    dropHere: 'Drop CVs here...',
    dragHere: 'Drag and drop CVs here',
    browse: 'or click to browse - PDF and DOCX - Max {count} files',
    rankCvs: 'Rank {count} CV{plural} with AI',
    rankNoCount: 'Rank CVs with AI',
    freeNote: 'Free - No signup required - Results as soon as analysis finishes',
    processingTitle: 'AI is analysing your CVs',
    processingFile: 'Processing: {file}',
    almostDone: 'Almost done...',
    processingSteps: [
      'Extracting text from CVs...',
      'Identifying skills and experience...',
      'Matching against job requirements...',
      'Calculating match scores...',
    ],
    doneTitle: 'Your CVs are ranked!',
    processedSuccessfully: '{count} CVs processed successfully',
    score: 'Score: {score}',
    seeRankings: 'See full rankings',
    saveNote: 'Sign up free to save results and process more CVs',
  },
  anonResults: {
    confidence: {
      veryHigh: 'Very high match',
      high: 'High match',
      moderate: 'Moderate match',
      low: 'Low match',
    },
    oneWorthReviewing: '1 candidate worth reviewing first',
    manyWorthReviewing: '{count} candidates worth reviewing first',
    basedOnRequirements: 'based on your job requirements',
    highestScore: 'Highest match score',
    reviewFirst: 'Review first',
    candidateFallback: 'Candidate {number}',
    matchedSkills: 'Matched skills:',
    more: '+{count} more',
    higherScore: 'Higher match score',
    gapsDetected: 'Significant gaps detected - review carefully',
    match: '{score}% match',
    experience: 'Experience',
    years: '{count} years',
    education: 'Education',
    matchedSkillsHeading: 'MATCHED SKILLS',
    missingSkillsHeading: 'MISSING SKILLS',
    saveFound: 'You have found {count} ranked candidate{plural} worth reviewing. Save them.',
    saveExpired: 'Save your results before they expire',
    nameJoiner: ' and ',
    createAccountFound: 'Create a free account to save {names}, upload more CVs, shortlist and export your results.',
    createAccountExpired: 'Create a free account to save these rankings, upload more CVs and export your shortlist.',
    freeAccountTitle: 'WITH A FREE ACCOUNT YOU GET',
    accountBenefits: [
      'Results saved to your account',
      'Shortlist and review candidates',
      'Upload more CVs',
      'Export ranked list to CSV',
      'Create multiple job roles',
    ],
    saveContinue: 'Save results and continue free',
    newUpload: 'New upload',
    noCard: 'No credit card - Sign up to save these results to your account',
    expiredMessage: 'Your anonymous shortlist has expired. Start a new upload to rank candidates again.',
    loadError: 'We could not load your anonymous shortlist. Please try again.',
    loading: 'Loading your results...',
    startNewUpload: 'Start a new upload',
    tryAgain: 'Try again',
    noCandidates: 'No candidates could be analysed from your upload.',
    tryAnother: 'Try another upload',
    saveResultsFree: 'Save results free',
    candidatesRanked: '{count} candidates ranked',
    resultsFor: 'Results for: {jobTitle}',
    rankedCandidates: 'Your ranked candidates',
    rankedByScore: 'Ranked by AI match score - Highest match score at the top',
  },
  common: {
    loading: 'Loading...',
    cancel: 'Cancel',
    save: 'Save',
    back: 'Back',
    upgrade: 'Upgrade',
    upgradePlan: 'Upgrade plan',
  },
  authenticated: {
    app: {
      pages: {
        jobs: {
          title: 'Jobs',
          subtitle: 'Manage your active job openings',
        },
        settings: {
          title: 'Settings',
          subtitle: 'Manage your account and billing',
        },
        admin: {
          title: 'Admin Panel',
          subtitle: 'Manage all users',
        },
        help: {
          title: 'How it works',
          subtitle: 'Understanding AI CV screening',
        },
        billingSuccess: {
          title: 'Upgrade successful',
          subtitle: 'Your billing update is back in the app',
        },
        fallback: {
          title: 'WorkflowPro',
          subtitle: '',
        },
      },
      topBar: {
        jobUsage: "You're using {percent}% of your job slots.",
        cvUsage: "You've used {percent}% of your monthly CVs.",
        upgradeMore: 'Upgrade to unlock more.',
        upgradeNow: 'Upgrade now',
        closeBanner: 'Close upgrade banner',
      },
      sidebar: {
        productSubtitle: 'CV Screening',
        jobs: 'Jobs',
        settings: 'Settings',
        adminPanel: 'Admin Panel',
        admin: 'Admin',
        help: 'Help',
        howItWorks: 'How it works',
        upgrade: 'Upgrade',
        overLimit: 'Over limit',
        platformAdmin: 'Platform Admin',
        adminAccess: 'Platform admin access is unrestricted. Usage limits do not apply.',
        usage: 'Usage',
        activeJobs: 'Active Jobs',
        cvsThisMonth: 'CVs this month',
        upgradePlan: 'Upgrade plan',
        logOut: 'Log out',
      },
    },
    badges: {
      status: {
        active: 'Active',
        archived: 'Archived',
        closed: 'Closed',
        pending: 'Pending',
        shortlisted: 'Shortlisted',
        rejected: 'Rejected',
        processing: 'Processing',
      },
      role: {
        owner: 'Owner',
        admin: 'Admin',
        member: 'Member',
      },
      score: {
        excellent: 'Excellent',
        good: 'Good',
        partial: 'Partial',
        poor: 'Poor',
      },
    },
    recommendations: {
      strong: 'Review first',
      good: 'Worth reviewing',
      partial: 'Review carefully',
      weak: 'Significant gaps',
    },
    dashboard: {
      toasts: {
        deleteForbidden: 'Only owners and admins can delete jobs',
        archiveForbidden: 'Only owners and admins can archive jobs',
        createForbidden: 'Only owners and admins can create jobs',
        deleted: 'Job deleted successfully',
        deleteFailed: 'Failed to delete job',
        archived: 'Job archived',
        archiveFailed: 'Failed to archive job',
      },
      jobLimit: 'You have reached your job limit on the {plan} plan. Upgrade to create more job openings.',
      cvLimit: 'You have used all your CVs for this month. Upgrade to process more.',
      activeJobs: 'Active Jobs',
      cvsThisMonth: 'CVs This Month',
      ofMax: 'of {max}',
      jobOpenings: 'Job Openings',
      filters: {
        active: 'Active',
        archived: 'Archived',
        closed: 'Closed',
      },
      newJob: 'New Job',
      emptyActive: 'No active jobs yet',
      emptyStatus: 'No {status} jobs',
      emptyActiveOwner: 'Create your first job opening to start screening CVs with AI',
      emptyActiveMember: 'An owner or admin can create the first job for this account.',
      emptyStatusDescription: 'You have no {status} job openings',
      createFirstJob: 'Create first job',
      candidates: '{count} candidate{plural}',
      topMatch: 'Top match: {score}%',
      archive: 'Archive',
      deleteTrigger: 'Upgrade your plan to create more job openings',
      deleteTitle: 'Delete job?',
      deleteMessage: 'Are you sure you want to delete "{title}"? This will permanently remove all candidates and cannot be undone.',
      delete: 'Delete',
    },
    newJob: {
      toasts: {
        createForbidden: 'Only owners and admins can create jobs',
        created: 'Job created successfully',
        failed: 'Failed to create job',
      },
      errors: {
        titleRequired: 'Job title is required',
        descriptionRequired: 'Job description is required',
        descriptionDetail: 'Add a more detailed description so WorkflowPro can compare CVs against the role context',
      },
      backToJobs: 'Back to jobs',
      title: 'Create new job',
      subtitle: 'A detailed job description gives WorkflowPro clearer role context for candidate review',
      details: 'Job details',
      jobTitle: 'Job Title',
      jobTitlePlaceholder: 'e.g. Senior Software Engineer',
      location: 'Location',
      locationPlaceholder: 'e.g. Paris, France',
      employmentType: 'Employment Type',
      employmentTypes: {
        fullTime: 'Full-time',
        partTime: 'Part-time',
        contract: 'Contract',
        freelance: 'Freelance',
        internship: 'Internship',
      },
      jobDescription: 'Job description',
      descriptionPlaceholder: 'Paste your full job description here. Include responsibilities, the team context, and what success looks like in this role...',
      characters: '{count} characters',
      requirements: 'Requirements and skills',
      keyRequirements: 'Key requirements',
      optionalContext: '(optional context for candidate review)',
      requirementsPlaceholder: 'List specific skills, qualifications and requirements:\n- 5+ years Python experience\n- AWS certified\n- Team leadership experience\n- Fluent English',
      submit: 'Create job and start screening',
      tipsTitle: 'Tips for clearer ranking',
      tips: {
        detailedDescription: 'Provide a detailed job description',
        mustHave: 'Include must-have requirements',
        seniority: 'Describe the seniority level and scope',
        tools: 'List important tools and platforms',
        responsibilities: 'Include responsibilities and success outcomes',
        consistentContext: 'Clear role detail helps WorkflowPro compare CVs against the same requirements',
      },
      scoringTitle: 'How candidate scoring works',
      scoringItems: {
        domain: 'Core domain fit',
        mustHave: 'Must-have requirements',
        seniority: 'Seniority / experience fit',
        responsibility: 'Responsibility and scope',
        tools: 'Tools / platform fit',
        reviewPoints: 'Red flags / gaps',
        explanation: 'Recruiter-style explanation',
      },
    },
    jobDetailsPanel: {
      title: 'Job details',
      hide: 'Hide the role brief and requirements',
      show: 'Show title, description, and requirements',
      untitled: 'Untitled job',
      lastUpdated: 'Last updated {date}',
      description: 'Description',
      noDescription: 'No description provided.',
      requirements: 'Requirements',
      editRequirements: 'Edit requirements',
      placeholder: 'List the must-have skills, seniority, tools, and scope for this role...',
      noRequirements: 'No requirements provided yet.',
      noAutoRescore: 'Changing requirements does not automatically re-score candidates.',
      updateFailed: 'Failed to update requirements',
      tip: 'Tip',
      tipCopy: 'Clear requirements improve ranking, but equivalent wording is understood.',
      control: 'Control',
      controlCopy: 'If requirements change, you choose whether to re-score existing candidates.',
      rescoreTitle: 'Re-score existing candidates?',
      rescoreDescription: 'Requirements changed. Save the new job requirements and re-score the existing candidates now, or save the requirements only and leave candidate scores as they are.',
      saveOnly: 'Save only',
      saveAndRescore: 'Save and re-score',
      saveRequirements: 'Save requirements',
    },
    jobDetail: {
      toasts: {
        loadCandidateFailed: 'Failed to load candidate details',
        uploadFailed: 'Upload failed',
        processed: '{count} CV{plural} processed successfully',
        candidateStatus: 'Candidate {status}',
        updateStatusFailed: 'Failed to update status',
        exportAll: 'Exported all {count} candidates',
        exportShortlisted: 'Exported {count} shortlisted candidates',
        exportSelected: 'Exported {count} selected candidates',
        exportFailed: 'Export failed',
        requirementsSavedRescore: 'Requirements saved and candidates re-scored',
        requirementsSaved: 'Requirements saved',
        rescoreWarning: 'Re-score completed with warnings',
        requirementsFailed: 'Failed to update requirements',
        compareLimit: 'Maximum 4 candidates can be compared',
        compareMinimum: 'Select at least two candidates to compare',
      },
      backToJobs: 'Back to jobs',
      loadingJob: 'Loading...',
      candidatesCount: '{count} candidate{plural}',
      topScore: 'Top score: {score}',
      refresh: 'Refresh',
      export: 'Export',
      uploadCvs: 'Upload CVs',
      scoreBands: {
        excellent: 'Excellent',
        good: 'Good',
        partial: 'Partial',
        poor: 'Poor',
      },
      exportTitle: 'Export candidates',
      exportSubtitle: 'Choose which candidates to include in the CSV export.',
      exportDescription: 'Exporting selected or shortlisted candidates keeps the CSV focused. Choose export all only when you need the full job list.',
      exportSelected: 'Export selected ({count})',
      exportShortlisted: 'Export shortlisted ({count})',
      exportAll: 'Export all ({count})',
      compareSelected: '{count} candidates selected for comparison',
      compareNow: 'Compare now',
      clear: 'Clear',
      filters: {
        all: 'All',
        shortlisted: 'Shortlisted',
        pending: 'Pending',
        rejected: 'Rejected',
      },
      candidateTotal: '{count} candidate{plural}',
      loadingCandidates: 'Loading candidates...',
      noCandidates: 'No candidates yet',
      noCandidatesDescription: 'Upload CVs to start ranking candidates for this job',
      table: {
        candidate: 'Candidate',
        score: 'Score',
        experience: 'Experience',
        skills: 'Skills',
        actions: 'Actions',
      },
      unknownCandidate: 'Unknown',
      shortlisted: 'Shortlisted',
      yearsShort: '{count} yrs',
      view: 'View',
      shortlist: 'Shortlist',
      compare: 'Compare',
      candidateFallback: 'Candidate',
      loadingCandidateDetails: 'Loading candidate details...',
      matchScore: 'Match score: {score}',
      candidateDetailsFailed: 'Candidate details could not be loaded.',
      uploadTitle: 'Upload CVs',
      supportedFiles: 'PDF and DOCX supported',
      limitReached: 'Monthly CV limit reached',
      limitReachedDescription: 'Upgrade your plan to process more CVs this month.',
      dropOrBrowse: 'Drop CVs here or click to browse',
      processing: 'Processing...',
      duplicate: 'Duplicate',
      error: 'Error',
      done: 'Done',
      processWithAi: 'Process {count} CV{plural} with AI',
      processNoCount: 'Process CVs with AI',
      detail: {
        aiMatchScore: 'AI match score',
        recommendation: 'Recommendation: {label}',
        model: 'Model: {label}',
        summary: 'AI SUMMARY',
        whyScore: 'WHY THIS SCORE',
        scoreBreakdown: 'SCORE BREAKDOWN',
        matchedEvidence: 'MATCHED EVIDENCE',
        matchedExcerpt: 'MATCHED EXCERPT',
        missingEvidence: 'MISSING EVIDENCE',
        redFlags: 'RED FLAGS',
        cvDetails: 'CV DETAILS',
        workHistory: 'WORK HISTORY',
        strengths: 'STRENGTHS',
        currentRole: 'Current role',
        email: 'Email',
        phone: 'Phone',
        experience: 'Experience',
        education: 'Education',
        certifications: 'Certifications',
        cvFile: 'CV file',
        lastUpdated: 'Last updated',
        years: '{count} year{plural}',
        previousRole: 'Previous role',
        workHistoryAt: '{role} at {company}',
        removeShortlist: 'Remove from shortlist',
        addShortlist: 'Add to shortlist',
        unreject: 'Unreject candidate',
        reject: 'Reject candidate',
        removeCompare: 'Remove from compare',
        addCompare: 'Add to compare',
      },
    },
    comparison: {
      toasts: {
        minimum: 'Select at least two candidates to compare',
        notEnough: 'Could not load enough candidates to compare',
        loadFailed: 'Failed to load candidate comparison',
        requirementsSavedRescore: 'Requirements saved and candidates re-scored',
        requirementsSaved: 'Requirements saved',
        rescoreWarning: 'Re-score completed with warnings',
        requirementsFailed: 'Failed to update requirements',
      },
      backToJob: 'Back to job',
      title: 'Candidate comparison',
      fallbackJob: 'This job',
      selected: '{count} candidates selected',
      close: 'Close comparison',
      loading: 'Loading candidate comparison...',
      unknownCandidate: 'Unknown candidate',
      roleNotProvided: 'Role not provided',
      recommendation: 'Recommendation: {label}',
      yearsExperience: '{count} year{plural} experience',
      summary: 'SUMMARY',
      scoreBreakdown: 'SCORE BREAKDOWN',
      strengths: 'STRENGTHS',
      matchedEvidence: 'MATCHED EVIDENCE',
      gaps: 'GAPS',
      evidenceExcerpt: 'EVIDENCE EXCERPT',
      email: 'Email',
      phone: 'Phone',
      lastUpdated: 'Last updated',
      emptyTitle: 'No candidates available to compare',
      emptyDescription: 'Return to the job detail page and select at least two candidates.',
    },
    score: {
      overall: 'Overall Score',
      aiEvaluation: 'AI evaluation',
      noAiExplanation: 'No AI explanation available.',
      redFlags: 'Red flags:',
      none: 'None',
      mustHaveDetail: 'Must-have detail',
      ratio: 'ratio {value}',
      noMustHaveExplanation: 'No must-have explanation available.',
      hardTerms: 'Hard terms:',
      matched: 'Matched:',
      supporting: 'Supporting:',
      labels: {
        coreDomainFit: 'Core domain fit',
        mustHaveMatch: 'Must-have match',
        seniorityFit: 'Seniority fit',
        responsibilityFit: 'Responsibility fit',
        toolsFit: 'Tools fit',
        redFlags: 'Red flags',
        ruleFallback: 'Rule fallback',
        guardrails: 'Guardrails',
        skillMatch: 'Skill match',
        mustHave: 'Must-have',
        experience: 'Experience',
        ruleScore: 'Rule score',
        calibration: 'Calibration',
        semanticScore: 'Semantic score',
        coreDomain: 'Core domain',
        roleTitle: 'Role title',
        responsibility: 'Responsibility',
        seniority: 'Seniority',
        toolPlatform: 'Tool/platform',
        geographyRegulatory: 'Geography/regulatory',
        credential: 'Credential',
        penalty: 'Penalty',
        education: 'Education',
        keywordMatch: 'Keyword Match',
      },
      descriptions: {
        coreDomainFit: 'AI judged direct domain fit at {score}/100.',
        mustHaveMatch: 'AI judged hard requirement fit at {score}/100.',
        seniorityFit: 'AI judged seniority fit at {score}/100.',
        responsibilityFit: 'AI judged responsibility and scope fit at {score}/100.',
        toolsFit: 'AI judged tools and platform fit at {score}/100.',
        noMajorRedFlags: 'No major red flags were identified.',
        excellentSkill: 'Excellent skill alignment with job requirements',
        goodSkill: 'Good skill match with some gaps',
        partialSkill: 'Partial skill match - review skill gaps',
        lowSkill: 'Low skill match - candidate may need upskilling',
        highExperience: '{years} years experience - highly experienced',
        strongExperience: '{years} years experience - strong background',
        developingExperience: '{years} years experience - developing professional',
        earlyExperience: '{years} years experience - early career',
        limitedExperience: 'Limited professional experience detected',
        degreeCerts: 'Degree and professional certifications found',
        degreeOnly: 'Degree found but no certifications detected',
        certsOnly: 'Certifications found but no degree detected',
        noEducation: 'No degree or certifications detected',
        closeKeyword: 'CV language closely matches the job description',
        goodKeyword: 'Good keyword alignment with job description',
        someKeyword: 'Some relevant keywords found but language differs',
        lowKeyword: 'Low keyword match - candidate may use different terminology',
        overallStrong: 'Strong match - review this candidate first',
        overallGood: 'Good match - worth reviewing',
        overallModerate: 'Moderate match - review profile carefully',
        overallWeak: 'Weak match - significant gaps identified',
      },
    },
  },
}
